import { Sigma, BookOpenText, Boxes, Compass, ListOrdered, ScanEye, Lightbulb, Brain } from "lucide-react";

const SUBTEST_META = [
  { key: "numerical_reasoning", name: "Numerical Reasoning", icon: Sigma },
  { key: "vocabulary", name: "Vocabulary", icon: BookOpenText },
  { key: "inductive_reasoning", name: "Inductive Reasoning", icon: Boxes },
  { key: "spatial_visualization", name: "Spatial Visualization", icon: Compass },
  { key: "sequential_reasoning", name: "Sequential Reasoning", icon: ListOrdered },
  { key: "visual_comparison_speed", name: "Visual Comparison Speed", icon: ScanEye },
];

const DICHOTOMY_LABELS = {
  structured_adaptive: ["Structured", "Adaptive"],
  independent_collaborative: ["Independent", "Collaborative"],
  detail_bigpicture: ["Detail-Oriented", "Big-Picture"],
  cautious_bold: ["Cautious", "Bold"],
};

function ScoreBar({ value }) {
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-700" style={{ width: `${value ?? 0}%` }} />
    </div>
  );
}

export function AptitudeReportView({ report }) {
  if (!report) return null;
  const scores = report.scores || {};
  const narrative = report.narrative || {};
  const results = scores.subtest_results || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="aptitude-report-view">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
            <Brain className="h-5 w-5 text-violet-600" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wide text-violet-600 font-medium">Aptitude Test Report</p>
            <p className="font-mono text-3xl font-bold text-slate-900" data-testid="aptitude-index-score">
              {scores.aptitude_index ?? "—"}<span className="text-sm text-slate-400 font-normal"> / 100 Aptitude Index</span>
            </p>
          </div>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed" data-testid="aptitude-report-snapshot">{narrative.snapshot}</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-5">Reasoning &amp; Processing Speed</h3>
        <div className="space-y-4">
          {SUBTEST_META.map((s) => {
            const r = results[s.key];
            if (!r) return null;
            const Icon = s.icon;
            return (
              <div key={s.key} data-testid={`aptitude-subtest-score-${s.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-700 flex items-center gap-2"><Icon className="h-3.5 w-3.5 text-slate-400" /> {s.name}</span>
                  <span className="font-mono text-sm font-semibold text-slate-800">{r.score}</span>
                </div>
                <ScoreBar value={r.score} />
              </div>
            );
          })}
        </div>
        {narrative.cognitive_strengths?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {narrative.cognitive_strengths.map((s, i) => (
              <span key={i} className="text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1">{s}</span>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="aptitude-ideation-card">
          <h3 className="font-heading text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-500" /> Idea Generation
          </h3>
          <p className="font-mono text-2xl font-bold text-slate-900 mb-2">
            {scores.creative_ideation_score ?? "—"}<span className="text-xs text-slate-400 font-normal"> / 100</span>
          </p>
          <ScoreBar value={scores.creative_ideation_score} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="aptitude-work-style-card">
          <h3 className="font-heading text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Brain className="h-4 w-4 text-cyan-600" /> Work Approach Style
          </h3>
          <div className="space-y-2.5">
            {Object.entries(scores.work_style_profile || {}).map(([d, p]) => (
              <div key={d} className="flex items-center justify-between text-xs text-slate-600" data-testid={`aptitude-work-style-${d}`}>
                <span>{DICHOTOMY_LABELS[d]?.[0]}</span>
                <span className="font-mono font-semibold text-cyan-700">{p.dominant_label} · {p.lean_pct}%</span>
                <span>{DICHOTOMY_LABELS[d]?.[1]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {narrative.development_note && (
        <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white">
          <p className="text-sm leading-relaxed" data-testid="aptitude-development-note">{narrative.development_note}</p>
        </div>
      )}
    </div>
  );
}
